import { useClerk } from "@clerk/react"
import { SUPPORT_EMAIL } from "@/lib/support"
import type { AccessFailure } from "@/utils/access-failure"

export default function AccessState({
  failure,
  retry,
  pending = false,
}: {
  failure: AccessFailure
  retry?: () => void
  pending?: boolean
}) {
  const { signOut } = useClerk()
  const authentication = failure.kind === "authentication"
  const denied = failure.kind === "denied"
  const inactive = failure.code === "ACCOUNT_INACTIVE"
  const permissionDenied = failure.code === "PERMISSION_DENIED"
  const title = authentication
    ? "Please sign in again"
    : denied
      ? inactive
        ? "Account inactive"
        : permissionDenied
          ? "Access denied"
          : "Access Not Available"
      : "Unable to verify access"
  const message = authentication
    ? "We could not authenticate your session. Sign in again to continue."
    : denied
      ? inactive
        ? "Your Vivid account is inactive. Contact support for help."
        : permissionDenied
          ? "You do not have permission to access this part of Vivid. Contact support for help."
          : "Your account does not have access to Vivid. Contact support if you believe this is a mistake."
      : "We could not verify your access. Check your connection and try again."

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#5D82ED] to-[#C08AF8] px-6">
      <div className="w-full max-w-md rounded-xl bg-white p-8 text-center shadow-2xl" role="alert">
        <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        <p className="mt-3 text-gray-600">{message}</p>
        {pending && (
          <p className="mt-3 text-sm text-gray-600" role="status">
            Retrying automatically…
          </p>
        )}
        <div className="mt-6 space-y-3">
          {retry && (
            <button
              type="button"
              disabled={pending}
              onClick={retry}
              className="block w-full rounded-lg bg-[#5A83EC] px-4 py-2 font-medium text-white disabled:opacity-50"
            >
              Try again
            </button>
          )}
          {denied && (
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="block w-full rounded-lg bg-[#5A83EC] px-4 py-2 font-medium text-white"
            >
              Contact Support
            </a>
          )}
          {(authentication || denied) && (
            <button
              type="button"
              onClick={() => void signOut({ redirectUrl: "/sign-in" })}
              className="block w-full rounded-lg border px-4 py-2 font-medium text-gray-700"
            >
              {authentication ? "Sign in again" : "Sign Out"}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
